/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */

import { MutableRef, Ref, useLayoutEffect, useState } from 'preact/hooks';
import { useTooltipControlled } from '../hooks/UNSAFE_useTooltip';
import { ItemInfo } from './Legend.type';
import { useUser } from '../hooks/UNSAFE_useUser';

type props = {
  text?: string;
  rootDimsRef: MutableRef<DOMRect | undefined>;
  focusedItemRef: Ref<HTMLDivElement>;
  focusedItemInfo: ItemInfo;
};

const DEFAULT_OFFSET = 8;

/**
 * Calculate the datatip offset for datatip when keyboard focus is active.
 * @param focusedItemRef The ref for currentItem node.
 * @param rootRef The ref for root of the legend.
 * @param currentItem The ItemInfo of the current keyboard focused item.
 * @returns
 */
const calculateOffset = (
  focusedItemRef: Ref<HTMLDivElement>,
  rootDimsRef: MutableRef<DOMRect | undefined>,
  currentItem: ItemInfo,
  isRtl: boolean
) => {
  let crossAxis = 0;
  let mainAxisOffset = DEFAULT_OFFSET;
  if (focusedItemRef.current && currentItem.isCurrent && rootDimsRef.current) {
    const rootDim = rootDimsRef.current;
    const itemDim = focusedItemRef.current.getBoundingClientRect();
    crossAxis = itemDim.x + itemDim.width / 2 - (rootDim.x + rootDim.width / 2);
    crossAxis = isRtl ? -1 * crossAxis : crossAxis;
    mainAxisOffset += rootDim.y - itemDim.y;
    mainAxisOffset = Math.min(
      DEFAULT_OFFSET,
      Math.max(mainAxisOffset, itemDim.height - rootDim.height + DEFAULT_OFFSET)
    );
  }
  return { crossAxis, mainAxis: mainAxisOffset };
};

export const useLegendTooltip = ({ text, rootDimsRef, focusedItemRef, focusedItemInfo }: props) => {
  const { direction } = useUser();
  const isRtl = direction === 'rtl';
  const [offset, setOffset] = useState<{ mainAxis?: number; crossAxis?: number }>(
    calculateOffset(focusedItemRef, rootDimsRef, focusedItemInfo, isRtl)
  );

  const [isOpen, setIsOpen] = useState<boolean>(false);

  useLayoutEffect(() => {
    setOffset(calculateOffset(focusedItemRef, rootDimsRef, focusedItemInfo, isRtl));
  }, [focusedItemInfo]);

  const anchor = focusedItemInfo?.isCurrent ? 'element' : 'pointer';
  const { tooltipContent, tooltipProps } = useTooltipControlled({
    text: text,
    position: 'top',
    isDisabled: false,
    variant: 'datatip',
    anchor: { x: anchor, y: anchor },
    offset: offset,
    isOpen,
    onToggle: ({ value }) => setIsOpen(value)
  });

  return { tooltipContent, tooltipProps };
};
